export type ClientConfidence = "verified" | "probable" | "unverified"

export type ClientIndustry = "pharma" | "all-industries" | "food-processing" | "manufacturing"

export type ClientCompany = {
  id: string
  rawName: string
  canonicalName: string
  industry: ClientIndustry
  confidence: ClientConfidence
  website?: string
  logoSrc: string
  logoSurface?: "light" | "dark"
  notes?: string
  verificationSources: string[]
}

export const rawClientExtractionFromPhotos = {
  pharma: [
    "Lupin Ltd",
    "Wockhardt Ltd",
    "FDC Ltd",
    "Warren Remedies",
    "Harmanjinochem",
    "R V Life Sciences",
    "Mylan Lab",
    "Curia India Pvt Ltd",
    "R.V. Life Sciences",
  ],
  allIndustries: [
    "Jollyboard",
    "Hyosung T&D India",
    "Radico NV",
    "Bajaj Auto",
    "Carlsberg",
    "ABInbev India Ltd",
    "Garware Hi-Tech Films",
  ],
  foodProcessing: ["Rajhans Milk", "Krishna Dairy", "Varun Brewerages", "Kirti Foods"],
  manufacturing: ["Ipca Labs", "Galaxy Lab", "Lilasons Industries Limited"],
} as const

export const clientCompanies: ClientCompany[] = [
  {
    id: "lupin-limited",
    rawName: "Lupin Ltd",
    canonicalName: "Lupin Limited",
    industry: "pharma",
    confidence: "verified",
    website: "https://www.lupin.com/",
    logoSrc: "/images/clients/pharma/lupin-limited.png",
    verificationSources: ["https://www.lupin.com/"],
  },
  {
    id: "wockhardt-limited",
    rawName: "Wockhardt Ltd",
    canonicalName: "Wockhardt Limited",
    industry: "pharma",
    confidence: "verified",
    website: "https://www.wockhardt.com/",
    logoSrc: "/images/clients/pharma/wockhardt-limited.png",
    verificationSources: ["https://www.wockhardt.com/"],
  },
  {
    id: "fdc-limited",
    rawName: "FDC Ltd",
    canonicalName: "FDC Limited",
    industry: "pharma",
    confidence: "verified",
    website: "https://www.fdcindia.com/",
    logoSrc: "/images/clients/pharma/fdc-limited.png",
    verificationSources: ["https://www.fdcindia.com/"],
  },
  {
    id: "warren-remedies",
    rawName: "Warren Remedies",
    canonicalName: "Warren Remedies Private Limited",
    industry: "pharma",
    confidence: "probable",
    logoSrc: "/images/clients/unverified/warren-remedies.svg",
    notes: "Official website/logo endpoint not reliably reachable; placeholder logo used.",
    verificationSources: [
      "https://www.indoamine.com/Indoco-and-Warren-Remedies-sign-definitive-agreement/",
    ],
  },
  {
    id: "harman-finochem",
    rawName: "Harmanjinochem",
    canonicalName: "Harman Finochem Limited",
    industry: "pharma",
    confidence: "verified",
    website: "https://harmanfinochem.com/",
    logoSrc: "/images/clients/pharma/harman-finochem.png",
    verificationSources: ["https://harmanfinochem.com/"],
  },
  {
    id: "rv-lifesciences",
    rawName: "R V Life Sciences",
    canonicalName: "RV Lifesciences Limited",
    industry: "pharma",
    confidence: "verified",
    website: "https://rvlifesciences.com/",
    logoSrc: "/images/clients/pharma/rv-lifesciences.png",
    verificationSources: ["https://rvlifesciences.com/"],
  },
  {
    id: "mylan-laboratories",
    rawName: "Mylan Lab",
    canonicalName: "Mylan Laboratories Limited (now Viatris)",
    industry: "pharma",
    confidence: "probable",
    website: "https://www.viatris.com/",
    logoSrc: "/images/clients/pharma/mylan-viatris.svg",
    notes: "Mylan brand is now part of Viatris; Viatris logo used.",
    verificationSources: ["https://www.viatris.com/en/about-us/our-history"],
  },
  {
    id: "curia-india",
    rawName: "Curia India Pvt Ltd",
    canonicalName: "Curia India Private Limited",
    industry: "pharma",
    confidence: "verified",
    website: "https://curiaglobal.com/india/",
    logoSrc: "/images/clients/pharma/curia-india.svg",
    verificationSources: ["https://curiaglobal.com/india/"],
  },
  {
    id: "jolly-board",
    rawName: "Jollyboard",
    canonicalName: "Jolly Board Private Limited",
    industry: "all-industries",
    confidence: "verified",
    website: "https://www.jollyboard.com/",
    logoSrc: "/images/clients/all-industries/jolly-board.png",
    verificationSources: ["https://www.jollyboard.com/"],
  },
  {
    id: "hyosung-td-india",
    rawName: "Hyosung T&D India",
    canonicalName: "Hyosung T&D India Private Limited",
    industry: "all-industries",
    confidence: "verified",
    website: "https://www.hyosungtndindia.in/",
    logoSrc: "/images/clients/all-industries/hyosung-td-india.svg",
    verificationSources: ["https://www.hyosungtndindia.in/"],
  },
  {
    id: "radico-nv",
    rawName: "Radico NV",
    canonicalName: "Radico NV Distilleries Maharashtra Limited",
    industry: "all-industries",
    confidence: "verified",
    website: "https://www.radiconv.co.in/",
    logoSrc: "/images/clients/all-industries/radico-nv.png",
    verificationSources: ["https://www.radiconv.co.in/"],
  },
  {
    id: "bajaj-auto",
    rawName: "Bajaj Auto",
    canonicalName: "Bajaj Auto Limited",
    industry: "all-industries",
    confidence: "verified",
    website: "https://www.bajajauto.com/",
    logoSrc: "/images/clients/all-industries/bajaj-auto.png",
    verificationSources: ["https://www.bajajauto.com/"],
  },
  {
    id: "carlsberg-india",
    rawName: "Carlsberg",
    canonicalName: "Carlsberg India Private Limited",
    industry: "all-industries",
    confidence: "verified",
    website: "https://www.carlsbergindia.com/",
    logoSrc: "/images/clients/all-industries/carlsberg-india.png",
    verificationSources: ["https://www.carlsbergindia.com/"],
  },
  {
    id: "ab-inbev-india",
    rawName: "ABInbev India Ltd",
    canonicalName: "AB InBev India (Anheuser-Busch InBev)",
    industry: "all-industries",
    confidence: "verified",
    website: "https://www.ab-inbev.com/",
    logoSrc: "/images/clients/all-industries/ab-inbev-india.svg",
    verificationSources: ["https://www.ab-inbev.com/"],
  },
  {
    id: "garware-hitech-films",
    rawName: "Garware Hi-Tech Films",
    canonicalName: "Garware Hi-Tech Films Limited",
    industry: "all-industries",
    confidence: "verified",
    website: "https://www.garwarehitechfilms.com/",
    logoSrc: "/images/clients/all-industries/garware-hitech-films.png",
    verificationSources: ["https://www.garwarehitechfilms.com/"],
  },
  {
    id: "rajhans-milk",
    rawName: "Rajhans Milk",
    canonicalName: "Rajhans Milk Foods Limited",
    industry: "food-processing",
    confidence: "verified",
    website: "https://www.rajhansmilk.com/",
    logoSrc: "/images/clients/food-processing/rajhans-milk.png",
    verificationSources: ["https://www.rajhansmilk.com/"],
  },
  {
    id: "krishna-dairy",
    rawName: "Krishna Dairy",
    canonicalName: "Krishna Dairy",
    industry: "food-processing",
    confidence: "probable",
    website: "https://krishnadairy.com/",
    logoSrc: "/images/clients/food-processing/krishna-dairy.png",
    notes: "Site title appears as 'Krishna Diary'; legal name should be confirmed.",
    verificationSources: ["https://krishnadairy.com/"],
  },
  {
    id: "varun-beverages",
    rawName: "Varun Brewerages",
    canonicalName: "Varun Beverages Limited",
    industry: "food-processing",
    confidence: "verified",
    website: "https://www.varunbeverages.com/",
    logoSrc: "/images/clients/food-processing/varun-beverages.png",
    verificationSources: ["https://www.varunbeverages.com/"],
  },
  {
    id: "kirti-foods",
    rawName: "Kirti Foods",
    canonicalName: "Kirti Foods",
    industry: "food-processing",
    confidence: "probable",
    website: "https://kirtifood.com/",
    logoSrc: "/images/clients/unverified/kirti-foods.svg",
    notes: "Using text placeholder until official brand logo is shared.",
    verificationSources: ["https://kirtifood.com/"],
  },
  {
    id: "ipca-laboratories",
    rawName: "Ipca Labs",
    canonicalName: "Ipca Laboratories Limited",
    industry: "manufacturing",
    confidence: "verified",
    website: "https://www.ipca.com/",
    logoSrc: "/images/clients/manufacturing/ipca-laboratories.svg",
    verificationSources: ["https://www.ipca.com/"],
  },
  {
    id: "galaxy-laboratories",
    rawName: "Galaxy Lab",
    canonicalName: "Galaxy Laboratories Private Limited",
    industry: "manufacturing",
    confidence: "probable",
    website: "https://galaxylaboratories.com/",
    logoSrc: "/images/clients/manufacturing/galaxy-laboratories.png",
    notes: "Downloaded brand image from current official website.",
    verificationSources: ["https://galaxylaboratories.com/"],
  },
  {
    id: "lilasons-industries-limited",
    rawName: "Lilasons Industries Limited",
    canonicalName: "Lilasons Industries Limited",
    industry: "manufacturing",
    confidence: "probable",
    logoSrc: "/images/clients/unverified/lilasons-industries-limited.svg",
    notes: "Name confirmed from user correction; logo placeholder retained pending official brand file.",
    verificationSources: [
      "https://www.zaubacorp.com/company/LILASONS-INDUSTRIES-LIMITED/U15147MH1996PLC102200",
    ],
  },
]

export type ClientIndustryGroup = {
  id: ClientIndustry
  label: string
  marqueeSeconds: number
  direction: "left" | "right"
  clients: ClientCompany[]
}

const byIndustry = (industry: ClientIndustry) => clientCompanies.filter((client) => client.industry === industry)

export const clientIndustryGroups: ClientIndustryGroup[] = [
  {
    id: "pharma",
    label: "Pharma",
    marqueeSeconds: 34,
    direction: "left",
    clients: byIndustry("pharma"),
  },
  {
    id: "all-industries",
    label: "All Industries",
    marqueeSeconds: 38,
    direction: "right",
    clients: byIndustry("all-industries"),
  },
  {
    id: "food-processing",
    label: "Food Processing",
    marqueeSeconds: 32,
    direction: "left",
    clients: byIndustry("food-processing"),
  },
  {
    id: "manufacturing",
    label: "Manufacturing",
    marqueeSeconds: 30,
    direction: "right",
    clients: byIndustry("manufacturing"),
  },
]

export const clientVerificationCounts = {
  verified: clientCompanies.filter((client) => client.confidence === "verified").length,
  probable: clientCompanies.filter((client) => client.confidence === "probable").length,
  unverified: clientCompanies.filter((client) => client.confidence === "unverified").length,
}
